import { InputAdornment, Divider, Grid, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Chip, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CatalogueEntry } from 'models/catalogues'
import {
  formatCatalogueLabel,
  getFormattedCurrency,
  renderCatalogueOption,
} from 'lib/helpers/shared.helpers'
import {
  selectCatalogue,
  selectCreatePaymentComplement,
  selectSelectedInvoice,
} from 'lib/helpers/selectors'
import { useAppSelector } from 'store/hooks'
import { IAddPaymentComplementContainerProps } from 'containers/Invoices/PaymentComplements/AddPaymentComplementContainer/AddPaymentComplementContainer'
import { IUseAddPaymentComplementContainer } from 'containers/Invoices/PaymentComplements/AddPaymentComplementContainer/useAddPaymentComplementContainer'
import { IPaymentConceptoResponse, IPaymentTaxLine, IPaymentTotales } from 'models/invoices.models'
import Button from 'components/Shared/Button/Button'
import IOCustomForm from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'
import NumericField from 'components/FormComponents/IOComponents/NumericField/NumericField'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import DisplayTotal from 'components/Invoices/DisplayTotal/DisplayTotal'

type TPaymentComplementFormProps = IUseAddPaymentComplementContainer &
  Pick<IAddPaymentComplementContainerProps, 'version'>

const IMPUESTO_LABELS: Record<string, string> = {
  '001': 'ISR',
  '002': 'IVA',
  '003': 'IEPS',
}

const MAX_ROWS_BEFORE_GROUP = 3

interface ITaxDetailRow {
  groupKey: string
  tipo: 'Traslado' | 'Retención'
  impuesto: string
  tipoFactor: string
  porcentaje: number
  base: number
  importe: number
  agrupado: boolean
  conceptosAgrupados?: number
  noObjeto?: boolean
}

const applyGroupingAndSort = (rawRows: ITaxDetailRow[]): ITaxDetailRow[] => {
  const countByKey = new Map<string, number>()
  rawRows.forEach(r => countByKey.set(r.groupKey, (countByKey.get(r.groupKey) ?? 0) + 1))

  const individualRows: ITaxDetailRow[] = []
  const accumulators = new Map<string, ITaxDetailRow & { count: number }>()

  rawRows.forEach(row => {
    const total = countByKey.get(row.groupKey) ?? 1
    if (total > MAX_ROWS_BEFORE_GROUP) {
      if (!accumulators.has(row.groupKey)) {
        accumulators.set(row.groupKey, { 
          ...row, 
          agrupado: true, 
          count: 0,
          base: 0,
          importe: 0
        })
      }
      const acc = accumulators.get(row.groupKey)!
      acc.base += row.base
      acc.importe += row.importe
      acc.count += 1
    } else {
      individualRows.push(row)
    }
  })

  const groupedRows: ITaxDetailRow[] = Array.from(accumulators.values()).map(
    ({ count, ...row }) => ({ ...row, conceptosAgrupados: count })
  )

  const sortOrder = (r: ITaxDetailRow): string => {
    if (r.noObjeto) return '9'
    return `${r.tipo === 'Traslado' ? '0' : '1'}|${r.impuesto}|${(100 - r.porcentaje).toFixed(4)}`
  }

  return [...individualRows, ...groupedRows].sort((a, b) =>
    sortOrder(a).localeCompare(sortOrder(b))
  )
}

/**
 * Construye las filas de la tabla desde los arreglos de traslados y retenciones.
 * IMPORTANTE: Siempre recibe trasladosP y retencionesP (valores en MonedaP - moneda correcta).
 */
const buildRowsFromTaxLines = (
  traslados: IPaymentTaxLine[],
  retenciones: IPaymentTaxLine[]
): ITaxDetailRow[] => {
  const toRows = (
    lines: IPaymentTaxLine[],
    tipo: 'Traslado' | 'Retención'
  ): ITaxDetailRow[] =>
    lines.map(line => {
      const tasa = line.tipoFactor === 'Exento' ? 0 : (line.tasaOCuota ?? 0)
      return {
        groupKey: `${tipo}|${line.impuesto}|${line.tipoFactor}|${tasa.toFixed(6)}`,
        tipo,
        impuesto: IMPUESTO_LABELS[line.impuesto] ?? line.impuesto,
        tipoFactor: line.tipoFactor,
        porcentaje: tasa * 100,
        base: line.base ?? 0,
        importe: line.importe ?? 0,
        agrupado: false,
      }
    })

  return applyGroupingAndSort([
    ...toRows(traslados, 'Traslado'),
    ...toRows(retenciones, 'Retención'),
  ])
}

/**
 * Construye las filas de la tabla desde el arreglo de conceptos.
 * 
 * REGLAS:
 * 1. Itera y desglosa individualmente cada concepto en 'result.conceptos'
 * 2. Incluye obligatoriamente conceptos "No objeto de impuesto" (objetoImp === "01")
 * 3. Consolidación condicional: Si hay MÁS DE 3 conceptos idénticos
 *    (mismo objetoImp + misma combinación impuesto/factor/tasa):
 *    - 1-3 conceptos: renderiza individualmente
 *    - 4+ conceptos: consolida en un solo renglón
 * 4. Usa propiedades directas: base, totalTraslados, totalRetenciones, total
 */
const buildRowsFromConceptos = (
  conceptos: IPaymentConceptoResponse[]
): ITaxDetailRow[] => {
  const allRows: ITaxDetailRow[] = []

  conceptos.forEach(concepto => {
    // CASO 1: Concepto "No objeto de impuesto" (objetoImp === "01")
    if (concepto.objetoImp === '01') {
      allRows.push({
        groupKey: `NoObjeto|${concepto.concepto}`,
        tipo: 'Traslado',
        impuesto: 'No aplica',
        tipoFactor: 'No aplica',
        porcentaje: 0,
        base: concepto.base ?? 0,
        importe: 0,
        agrupado: false,
        noObjeto: true,
      })
      return
    }

    // CASO 2: Concepto con impuestos - procesar traslados
    concepto.traslados.forEach(traslado => {
      const tasa = traslado.tipoFactor === 'Exento' ? 0 : (traslado.tasaOCuota ?? 0)
      const groupKey = `Traslado|${traslado.impuesto}|${traslado.tipoFactor}|${tasa.toFixed(6)}|${concepto.objetoImp}`
      
      allRows.push({
        groupKey,
        tipo: 'Traslado',
        impuesto: IMPUESTO_LABELS[traslado.impuesto] ?? traslado.impuesto,
        tipoFactor: traslado.tipoFactor,
        porcentaje: tasa * 100,
        base: traslado.base ?? 0,
        importe: traslado.importe ?? 0,
        agrupado: false,
      })
    })

    // CASO 3: Concepto con impuestos - procesar retenciones
    concepto.retenciones.forEach(retencion => {
      const tasa = retencion.tipoFactor === 'Exento' ? 0 : (retencion.tasaOCuota ?? 0)
      const groupKey = `Retención|${retencion.impuesto}|${retencion.tipoFactor}|${tasa.toFixed(6)}|${concepto.objetoImp}`
      
      allRows.push({
        groupKey,
        tipo: 'Retención',
        impuesto: IMPUESTO_LABELS[retencion.impuesto] ?? retencion.impuesto,
        tipoFactor: retencion.tipoFactor,
        porcentaje: tasa * 100,
        base: retencion.base ?? 0,
        importe: retencion.importe ?? 0,
        agrupado: false,
      })
    })
  })

  // Aplica la lógica de agrupación/consolidación (agrupa si > 3 conceptos idénticos)
  return applyGroupingAndSort(allRows)
}

/**
 * Construye las filas de la tabla de desglose de impuestos.
 * 
 * PRIORIDAD:
 * 1. Si existen conceptos, usa buildRowsFromConceptos (renderizado individual + consolidación condicional)
 * 2. Si NO existen conceptos, usa trasladosP/retencionesP globales (fallback)
 */
const buildDetailedTaxRows = (
  conceptosResponse: IPaymentConceptoResponse[] | undefined,
  traslados: IPaymentTaxLine[],
  retenciones: IPaymentTaxLine[]
): ITaxDetailRow[] => {
  // PRIORIDAD 1: Si hay conceptos, renderizarlos individualmente con consolidación condicional
  if (conceptosResponse && conceptosResponse.length > 0) {
    return buildRowsFromConceptos(conceptosResponse)
  }

  // PRIORIDAD 2 (FALLBACK): Usar trasladosP y retencionesP globales
  return buildRowsFromTaxLines(traslados, retenciones)
}

const PorcentajeChip = ({
  porcentaje,
  tipoFactor,
  noObjeto,
}: {
  porcentaje: number
  tipoFactor: string
  noObjeto?: boolean
}) => {
  if (noObjeto) {
    return (
      <Chip
        label="No aplica"
        size="small"
        sx={{ fontSize: 11, bgcolor: '#f5f5f5', color: '#757575', fontWeight: 600 }}
      />
    )
  }
  if (tipoFactor === 'Exento') {
    return (
      <Chip
        label="Exento"
        size="small"
        sx={{ fontSize: 11, bgcolor: '#e8f5e9', color: '#388e3c', fontWeight: 600 }}
      />
    )
  }
  if (porcentaje === 0) {
    return (
      <Chip
        label="0%"
        size="small"
        sx={{ fontSize: 11, bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
      />
    )
  }
  return (
    <Chip
      label={`${porcentaje % 1 === 0 ? porcentaje.toFixed(0) : porcentaje.toFixed(2)}%`}
      size="small"
      sx={{ fontSize: 11, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }}
    />
  )
}

/**
 * Tabla de desglose de impuestos por concepto.
 * 
 * LÓGICA DE RENDERIZADO:
 * 1. PRIORIDAD 1: Si existen conceptos[], itera y desglosa cada concepto individualmente
 *    - Incluye OBLIGATORIAMENTE conceptos "No objeto de impuesto" (objetoImp === "01")
 *    - Muestra cada concepto con sus atributos: base, totalTraslados, totalRetenciones, total
 * 
 * 2. CONSOLIDACIÓN CONDICIONAL: Si hay MÁS DE 3 conceptos idénticos
 *    (mismo objetoImp + misma combinación impuesto/factor/tasa):
 *    - 1-3 conceptos idénticos: Renderiza individualmente
 *    - 4+ conceptos idénticos: Consolida en un solo renglón (suma de bases e importes)
 * 
 * 3. PRIORIDAD 2 (FALLBACK): Si NO hay conceptos[], usa trasladosP/retencionesP globales
 * 
 * MONEDA: Todos los valores se muestran en MonedaP (moneda del pago).
 * 
 * Referencia: /home/karla/workspace/sydney/syd-webapp-api/simmple/docs/API-PaymentTaxCalculator-Contract.md
 */
const TaxBreakdownTable = ({
  trasladosP,
  retencionesP,
  conceptos,
  currencyCode,
}: {
  trasladosP: IPaymentTaxLine[]
  retencionesP: IPaymentTaxLine[]
  conceptos?: IPaymentConceptoResponse[]
  currencyCode: string
}) => {
  // IMPORTANTE: Usar trasladosP y retencionesP (ya convertidos a MonedaP)
  // NO usar trasladosDR/retencionesDR (que están en MonedaDR)
  const rows = buildDetailedTaxRows(conceptos, trasladosP, retencionesP)

  if (rows.length === 0) return null

  return (
    <div className="table-taxes">
      <Table size="small" aria-label="payment-tax-breakdown-table">
        <TableHead>
          <TableRow className="table-header">
            <TableCell className="table-cell" sx={{ width: '12%' }}>Tipo</TableCell>
            <TableCell className="table-cell" sx={{ width: '13%' }}>Impuesto</TableCell>
            <TableCell className="table-cell" sx={{ width: '13%' }}>Factor</TableCell>
            <TableCell className="table-cell" sx={{ width: '12%' }}>Tasa</TableCell>
            <TableCell className="table-cell" sx={{ width: '25%' }}>Base ({currencyCode})</TableCell>
            <TableCell className="table-cell" align="right" sx={{ width: '25%' }}>Importe</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow className="table-row" key={`taxrow-${idx}`}>
              <TableCell className="table-cell" sx={{ color: '#555', fontSize: 12 }}>
                {row.noObjeto
                  ? <PorcentajeChip porcentaje={0} tipoFactor="—" noObjeto />
                  : row.tipo
                }
              </TableCell>
              <TableCell className="table-cell" sx={{ color: '#555', fontSize: 12 }}>
                {row.noObjeto
                  ? <PorcentajeChip porcentaje={0} tipoFactor="—" noObjeto />
                  : row.impuesto
                }
              </TableCell>
              <TableCell className="table-cell" sx={{ color: '#555', fontSize: 12 }}>
                {row.noObjeto
                  ? <PorcentajeChip porcentaje={0} tipoFactor="—" noObjeto />
                  : row.tipoFactor
                }
              </TableCell>
              <TableCell className="table-cell" sx={{ color: '#555', fontSize: 12 }}>
                <PorcentajeChip
                  porcentaje={row.porcentaje}
                  tipoFactor={row.tipoFactor}
                  noObjeto={row.noObjeto}
                />
              </TableCell>
              <TableCell className="table-cell" sx={{ color: '#555', fontSize: 12 }}>
                {getFormattedCurrency(row.base)}
                {row.agrupado && row.conceptosAgrupados !== undefined && (
                  <Tooltip
                    title={`Suma de ${row.conceptosAgrupados} conceptos con el mismo impuesto`}
                    arrow
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: 14, ml: 0.5, color: '#999', verticalAlign: 'middle', cursor: 'help' }}
                    />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell className="table-cell" align="right" sx={{ color: '#555', fontSize: 12 }}>
                {row.noObjeto || row.tipoFactor === 'Exento'
                  ? <span style={{ color: '#bbb' }}>{getFormattedCurrency(0)}</span>
                  : getFormattedCurrency(row.importe)
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}



/**
 * Mapea códigos de impuesto a etiquetas legibles
 */
const IMPUESTO_LABELS_TOTALES: Record<string, string> = {
  '001': 'ISR',
  '002': 'IVA',
  '003': 'IEPS',
}

/**
 * Formatea la tasa/cuota como porcentaje o "Exento"
 */
const formatTasaLabel = (tipoFactor: string, tasaOCuota?: number): string => {
  if (tipoFactor === 'Exento') return ' Exento'
  if (tasaOCuota === undefined || tasaOCuota === null) return ''
  const pct = tasaOCuota * 100
  return ` ${pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2)}%`
}

/**
 * Componente TotalesRow - ESTRUCTURA EN DOS SECCIONES
 * 
 * SECCIÓN 1: Totales en MonedaP (USD, EUR, MXN)
 * - Mapea trasladosP y retencionesP del backend
 * - Muestra Monto Total de Pagos en MonedaP (result.montoP)
 * 
 * SECCIÓN 2: Fila Final en MXN (solo si MonedaP != MXN)
 * - Monto Total de Pagos (MXN): result.totales.MontoTotalPagos
 * 
 * Referencia: /home/karla/workspace/sydney/syd-webapp-api/simmple/docs/API-PaymentTaxCalculator-Contract.md
 */
const TotalesRow = ({
  trasladosP,
  retencionesP,
  montoP,
  totales,
  currencyCode,
}: {
  trasladosP: IPaymentTaxLine[]
  retencionesP: IPaymentTaxLine[]
  montoP: number
  totales: IPaymentTotales
  currencyCode: string
}) => {
  // ===== SECCIÓN 1: TOTALES EN MonedaP =====
  // Agrupar traslados por impuesto + factor + tasa
  const trasladosMap = new Map<string, { label: string; base: number; importe: number }>()
  
  trasladosP.forEach(t => {
    const key = `${t.impuesto}|${t.tipoFactor}|${t.tasaOCuota}`
    const impuestoLabel = IMPUESTO_LABELS_TOTALES[t.impuesto] || t.impuesto
    const tasaLabel = formatTasaLabel(t.tipoFactor, t.tasaOCuota)
    const label = `${impuestoLabel} Trasladado${tasaLabel}`
    
    const existing = trasladosMap.get(key)
    if (existing) {
      existing.base += t.base ?? 0
      existing.importe += t.importe ?? 0
    } else {
      trasladosMap.set(key, {
        label,
        base: t.base ?? 0,
        importe: t.importe ?? 0,
      })
    }
  })

  // Agrupar retenciones por impuesto
  const retencionesMap = new Map<string, { label: string; importe: number }>()
  
  retencionesP.forEach(r => {
    const key = r.impuesto
    const impuestoLabel = IMPUESTO_LABELS_TOTALES[r.impuesto] || r.impuesto
    const label = `Ret. ${impuestoLabel}`
    
    const existing = retencionesMap.get(key)
    if (existing) {
      existing.importe += r.importe ?? 0
    } else {
      retencionesMap.set(key, {
        label,
        importe: r.importe ?? 0,
      })
    }
  })

  // ===== SECCIÓN 2: FILA FINAL EN MXN (solo si MonedaP != MXN) =====
  const isForeignCurrency = currencyCode !== 'MXN'
  const montoTotalPagosMXN = totales?.MontoTotalPagos ? parseFloat(totales.MontoTotalPagos) : 0

  return (
    <>
      {/* SECCIÓN 1: TOTALES EN MonedaP */}
      {Array.from(trasladosMap.values()).map((traslado, idx) => (
        <TableRow key={`traslado-${idx}`} className="table-row">
          <TableCell className="table-cell" colSpan={3}>
            {traslado.label} ({currencyCode})
          </TableCell>
          <TableCell className="table-cell" align="right">
            {getFormattedCurrency(traslado.importe)}
          </TableCell>
        </TableRow>
      ))}

      {Array.from(retencionesMap.values()).map((retencion, idx) => (
        <TableRow key={`retencion-${idx}`} className="table-row">
          <TableCell className="table-cell" colSpan={3} sx={{ color: '#c62828' }}>
            {retencion.label} ({currencyCode})
          </TableCell>
          <TableCell className="table-cell" align="right" sx={{ color: '#c62828' }}>
            −{getFormattedCurrency(retencion.importe)}
          </TableCell>
        </TableRow>
      ))}

      <TableRow className="table-row totals-row">
        <TableCell className="table-cell" colSpan={3}>
          <strong>Monto Total de Pagos ({currencyCode})</strong>
        </TableCell>
        <TableCell className="table-cell" align="right">
          <strong>{getFormattedCurrency(montoP)}</strong>
        </TableCell>
      </TableRow>

      {/* SECCIÓN 2: FILA FINAL EN MXN (solo si MonedaP != MXN) */}
      {isForeignCurrency && (
        <TableRow className="table-row totals-row" sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0' }}>
          <TableCell className="table-cell" colSpan={3}>
            <strong>Monto Total de Pagos (MXN)</strong>
          </TableCell>
          <TableCell className="table-cell" align="right">
            <strong>
              {montoTotalPagosMXN > 0 
                ? getFormattedCurrency(montoTotalPagosMXN)
                : '$0.00'
              }
            </strong>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

const PaymentComplementForm = ({
  addMutation,
  ip,
  isa,
  monto,
  montoTotalPagosDisplay,
  montoTotalPagosLabel,
  isInvoiceFullyPaid,
  isAmountExceedingBalance,
  calculatePreview,
  isCalculating,
  valid,
  version,
  saveValueToStore,
  setValid,
}: TPaymentComplementFormProps) => {
  const form = useAppSelector(selectCreatePaymentComplement)
  const formasPago = useAppSelector(selectCatalogue('formasPago'))
  const invoice = useAppSelector(selectSelectedInvoice)
  const monedas = useAppSelector(selectCatalogue('monedas'))

  const hasPreview = !!calculatePreview
  const hasTaxLines =
    (calculatePreview?.conceptos?.length ?? 0) > 0 ||
    (calculatePreview?.trasladosP?.length ?? 0) > 0 ||
    (calculatePreview?.retencionesP?.length ?? 0) > 0

  // ===== IDENTIFICACIÓN DE LAS 3 MONEDAS =====
  const monedaDR = invoice?.currency?.value ?? 'MXN'  // Moneda de la factura
  const monedaP = form?.moneda?.value ?? 'MXN'        // Moneda del pago
  
  // ===== MATRIZ COMPLETA DE 4 ESCENARIOS =====
  // 🟡 Escenario 2: Extranjera Misma Moneda (MonedaDR == MonedaP && MonedaP != MXN)
  const isEscenario2ExtranjeraMismaMoneda = monedaDR === monedaP && monedaP !== 'MXN'
  
  // 🔵 Escenario 3: Factura Extranjera Pagada en Pesos (MonedaDR != MXN && MonedaP == MXN)
  const isEscenario3FacturaExtranjeraPagoPesos = monedaDR !== 'MXN' && monedaP === 'MXN'
  
  // 🔴 Escenario 4: Tres Monedas Simultáneas (MonedaDR != MonedaP && MonedaP != MXN)
  const isEscenario4TresMonedas = monedaDR !== monedaP && monedaP !== 'MXN'

  // ===== CONTROL DE VISIBILIDAD DE CAMPOS =====
  // Mostrar "Equivalencia de Pago/Factura (MonedaP/DR → MonedaDR/MXN)" / equivalenciaDR
  // - Escenario 3: Muestra "Equivalencia de la Factura (USD → MXN)"
  // - Escenario 4: Muestra "Equivalencia de Pago (USD → EUR)"
  const showEquivalenciaDR = version === '4.0' && (isEscenario3FacturaExtranjeraPagoPesos || isEscenario4TresMonedas)
  
  // Label dinámico para equivalenciaDR
  const equivalenciaDRLabel = isEscenario3FacturaExtranjeraPagoPesos
    ? `Equivalencia de la Factura (${monedaDR} → MXN)`
    : `Equivalencia de Pago (${monedaP} → ${monedaDR})`

  // Mostrar "Tipo de cambio a Pesos (MonedaP → MXN)" / tipoCambioP
  // - Escenario 2: Extranjera Misma Moneda
  // - Escenario 4: Tres Monedas
  const showTipoCambioP = version === '4.0' && (isEscenario2ExtranjeraMismaMoneda || isEscenario4TresMonedas)

  const tableCurrencyCode = monedaP

  // ===== CONTROL DE CAMPOS DE TIPO DE CAMBIO EN SECCIÓN SUPERIOR (V3.3) =====
  // Mostrar el campo de tipo de cambio en la sección "Datos del pago"
  // Solo en v3.3 cuando MonedaP != MXN
  const showTipoCambioV33 = version === '3.3' && monedaP !== 'MXN'

  // ===== CÁLCULO DE GRID PARA CAMPOS DE MONTO =====
  // Contar campos visibles: Monto base + campos opcionales
  const montoFieldCount =
    1 +                           // Monto a pagar (MonedaP) - SIEMPRE
    (hasPreview && monedaP !== 'MXN' && calculatePreview?.montoP !== undefined ? 1 : 0) + // Monto calculado (MonedaP) - solo con preview en moneda extranjera
    (showEquivalenciaDR ? 1 : 0) + // Equivalencia DR o Factura
    (showTipoCambioP ? 1 : 0) +   // Tipo de cambio a MXN
    (hasPreview ? 1 : 0)          // Monto total con impuestos
  const montoFieldXs = (12 / montoFieldCount) as any

  return (
    <IOCustomForm
      onSubmit={async () => addMutation.mutate()}
      onValidity={(v: boolean) => setValid(v)}>
      <div id="section-1" className="content">
        <h3 className="title">(1) Recepción de pagos</h3>
        <Divider className="separator" />
        <p className="subtitle">Datos del pago</p>
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <NumericField
              name="fechaPago"
              label="Fecha de pago"
              value={form?.fechaPago}
              onValue={(v: string) => saveValueToStore('fechaPago', v)}
              validate={['isDateFormatD', 'isValidDateD']}
              format="date"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <AutocompleteField
              name="formaPago"
              label="Forma de pago"
              catalogue={formasPago}
              value={form?.formaPago}
              onValue={(v: CatalogueEntry) => saveValueToStore('formaPago', v)}
              getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
              getOptionSelected={(
                option: CatalogueEntry,
                value: CatalogueEntry
              ) => option.idCatalogValues === value.idCatalogValues}
              renderOption={(o: any) => renderCatalogueOption(o)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <AutocompleteField
              name="moneda"
              label="Moneda"
              catalogue={monedas}
              value={form?.moneda}
              onValue={(v: CatalogueEntry) => saveValueToStore('moneda', v)}
              getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
              getOptionSelected={(
                option: CatalogueEntry,
                value: CatalogueEntry
              ) => option.idCatalogValues === value.idCatalogValues}
              renderOption={(o: any) => renderCatalogueOption(o)}
              fullWidth
              required
            />
          </Grid>
          {showTipoCambioV33 && (
            <Grid item xs={4}>
              <NumericField
                name="tipoCambio"
                label={`Tipo de cambio a Pesos (${monedaP} → MXN)`}
                value={form?.tipoCambio}
                onValue={(v: string) => saveValueToStore('tipoCambio', v)}
                format="exchangeRate"
                fullWidth
                required
              />
            </Grid>
          )}
          {version === '3.3' && (
            <Grid item xs={4}>
              <NumericField
                name="monto"
                label="Monto"
                value={form?.monto}
                onValue={(v: string) => saveValueToStore('monto', v)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                format="money"
                fullWidth
                required
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <TextField
              fieldType="number"
              name="numeroOperacion"
              label="Número de operación (opcional)"
              value={form?.numeroOperacion}
              onValue={(v: string) => saveValueToStore('numeroOperacion', v)}
              fullWidth
            />
          </Grid>
        </Grid>

        {version === '4.0' && (
          <>
            <p className="subtitle">Monto del pago</p>
            <Divider className="separator" />
            <Grid container spacing={4} alignItems="flex-end">
              {/* Campo 1: Monto a pagar en MonedaP (SIEMPRE SE MUESTRA) */}
              <Grid item xs={montoFieldXs}>
                <NumericField
                  name="monto"
                  label={`Monto a pagar${monedaP !== 'MXN' ? ` (${monedaP})` : ''}`}
                  value={form?.monto}
                  onValue={(v: string) => saveValueToStore('monto', v)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  format="money"
                  fullWidth
                  required
                />
              </Grid>

              {/* Campo 2: Equivalencia DR o Factura (ESCENARIOS 3 y 4) */}
              {showEquivalenciaDR && (
                <Grid item xs={montoFieldXs}>
                  <NumericField
                    name="tipoCambioDR"
                    label={equivalenciaDRLabel}
                    value={form?.tipoCambioDR}
                    onValue={(v: string) => saveValueToStore('tipoCambioDR', v)}
                    format="exchangeRate"
                    fullWidth
                    required
                  />
                </Grid>
              )}

              {/* Campo 3: Tipo de Cambio a Pesos (ESCENARIOS 2 y 4) */}
              {showTipoCambioP && (
                <Grid item xs={montoFieldXs}>
                  <NumericField
                    name="tipoCambio"
                    label={`Tipo de cambio a Pesos (${monedaP} → MXN)`}
                    value={form?.tipoCambio}
                    onValue={(v: string) => saveValueToStore('tipoCambio', v)}
                    format="exchangeRate"
                    fullWidth
                    required
                  />
                </Grid>
              )}

              {/* Campo 4: Monto calculado por el backend (MonedaP) - SOLO SI HAY PREVIEW Y MonedaP != MXN */}
              {hasPreview && monedaP !== 'MXN' && calculatePreview?.montoP !== undefined && (
                <Grid item xs={montoFieldXs}>
                  <NumericField
                    name="montoPCalculado"
                    label={`Monto calculado (${monedaP})`}
                    value={String(calculatePreview.montoP)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    inputProps={{ readOnly: true }}
                    format="money"
                    disabled
                    fullWidth
                  />
                </Grid>
              )}

              {/* Campo 5: Monto Total con Impuestos (SIEMPRE CON PREVIEW) */}
              {hasPreview && (
                <Grid item xs={montoFieldXs}>
                  <NumericField
                    name="montoTotalPagos"
                    label={montoTotalPagosLabel}
                    value={String(montoTotalPagosDisplay)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      disableUnderline: true,
                    }}
                    inputProps={{ readOnly: true }}
                    format="money"
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
            {isCalculating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <CircularProgress size={16} />
                <span style={{ fontSize: 12, color: '#666' }}>Calculando...</span>
              </div>
            )}

            {hasTaxLines && (
              <Grid container spacing={4} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <p
                    style={{
                      margin: '0 0 8px 0',
                    fontSize: 12,
                    color: '#666',
                    fontStyle: 'italic',
                  }}
                >
                  Desglose de impuestos por concepto. Incluye todos los conceptos (incluyendo "No objeto de impuesto").
                  Cuando hay MÁS DE {MAX_ROWS_BEFORE_GROUP} conceptos idénticos (mismo tipo de impuesto, factor y tasa), 
                  se consolidan automáticamente en un solo renglón con la suma total.
                </p>
                  <TaxBreakdownTable
                    trasladosP={calculatePreview!.trasladosP}
                    retencionesP={calculatePreview!.retencionesP}
                    conceptos={calculatePreview!.conceptos}
                    currencyCode={tableCurrencyCode}
                  />
                </Grid>
              </Grid>
            )}

            {hasPreview && calculatePreview!.totales && (
              <Grid container spacing={4} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <div className="table-taxes">
                    <Table size="small" aria-label="payment-totals-table">
                      <TableHead>
                        <TableRow className="table-header">
                          <TableCell className="table-cell" colSpan={3}>Concepto</TableCell>
                          <TableCell className="table-cell" align="right">Monto</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TotalesRow
                          trasladosP={calculatePreview!.trasladosP || []}
                          retencionesP={calculatePreview!.retencionesP || []}
                          montoP={calculatePreview!.montoP || 0}
                          totales={calculatePreview!.totales}
                          currencyCode={monedaP}
                        />
                      </TableBody>
                    </Table>
                  </div>
                </Grid>
              </Grid>
            )}
          </>
        )}

        <p className="subtitle">Información Adicional</p>
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <TextField
              name="rfcOrigen"
              label="RFC Emisor de cuenta origen (opcional)"
              validate={['isCompanyRFC', 'isPersonRFC']}
              value={form?.rfcOrigen}
              onValue={(v: string) => saveValueToStore('rfcOrigen', v)}
              inputProps={{
                maxLength: 13,
                style: { textTransform: 'uppercase' },
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="rfcBeneficiario"
              label="RFC Emisor cuenta beneficiaria (opcional)"
              validate={['isCompanyRFC', 'isPersonRFC']}
              value={form?.rfcBeneficiario}
              onValue={(v: string) => saveValueToStore('rfcBeneficiario', v)}
              inputProps={{
                maxLength: 13,
                style: { textTransform: 'uppercase' },
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="nombreBanco"
              label="Nombre de banco (opcional)"
              value={form?.nombreBanco}
              onValue={(v: string) => saveValueToStore('nombreBanco', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="cuentaBeneficiario"
              label="Cuenta beneficiario (opcional)"
              value={form?.cuentaBeneficiario}
              onValue={(v: string) => saveValueToStore('cuentaBeneficiario', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="cuentaOrdenante"
              label="Cuenta ordenante (opcional)"
              value={form?.cuentaOrdenante}
              onValue={(v: string) => saveValueToStore('cuentaOrdenante', v)}
              fullWidth
            />
          </Grid>
        </Grid>
        <p className="subtitle" />
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <AutocompleteField
              name="tipoCadenaPago"
              label="Tipo cadena de pago (opcional)"
              catalogue={[]}
              value={form?.tipoCadenaPago}
              onValue={(v: CatalogueEntry) =>
                saveValueToStore('tipoCadenaPago', v)
              }
              getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
              renderOption={(o: any) => renderCatalogueOption(o)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="certificadoPago"
              label="Certificado de pago (opcional)"
              value={form?.certificadoPago}
              onValue={(v: string) => saveValueToStore('certificadoPago', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="cadenaPago"
              label="Cadena de pago (opcional)"
              value={form?.cadenaPago}
              onValue={(v: string) => saveValueToStore('cadenaPago', v)}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="selloPago"
              label="Sello de pago (opcional)"
              value={form?.selloPago}
              onValue={(v: string) => saveValueToStore('selloPago', v)}
              fullWidth
            />
          </Grid>
        </Grid>
      </div>
      <div id="section-2" className="content">
        <h3 className="title">(2) Documentos relacionados</h3>
        <Divider className="separator" />
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <TextField
              name="idDocumento"
              label="ID del documento"
              value={invoice?.uuid ? invoice.uuid.toUpperCase() : ''}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="serie"
              label="Serie"
              value={invoice?.serie}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="folio"
              label="Folio"
              value={invoice?.folio}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              name="moneda"
              label="Moneda"
              value={formatCatalogueLabel(invoice?.currency)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          {invoice?.currency.value !== 'MXN' && (
            <Grid item xs={4}>
              <NumericField
                name="equivalenciaDR"
                label="Tipo de cambio"
                value={invoice?.exchangeRate ?? ''}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                format="money"
                fullWidth
                required
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <TextField
              name="metodoPago"
              label="Método de pago"
              value={`${invoice?.paymentMethod?.value} - ${invoice?.paymentMethod?.label}`}
              InputProps={{ disableUnderline: true }}
              inputProps={{ readOnly: true }}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fieldType="number"
              name="numeroParcialidad"
              label="Número de parcialidad"
              validate={['isMoreThanZero']}
              value={form?.numeroParcialidad}
              onValue={(v: string) => saveValueToStore('numeroParcialidad', v)}
              fullWidth
              required={invoice?.paymentMethod?.value === 'PPD'}
            />
          </Grid>
        </Grid>
        <p className="subtitle" />
        <Divider className="separator" />
        <Grid container spacing={4}>

          <Grid item xs={4}>
            <NumericField
              name="importeSaldoAnterior"
              label="Importe saldo anterior"
              defaultValue={isa.toString()}
              value={form?.importeSaldoAnterior}
              onValue={(v: string) =>
                saveValueToStore('importeSaldoAnterior', v)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              format="money"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <NumericField
              name="importePagado"
              label="Importe pagado"
              defaultValue={ip.toString()}
              value={form?.importePagado}
              onValue={(v: string) => saveValueToStore('importePagado', v)}
              disabled={!form?.importeSaldoAnterior}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
              format="money"
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={4}>
            <NumericField
              name="importeInsoluto"
              label="Importe insoluto"
              value={isa.subtract(ip).toString()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                disableUnderline: true,
              }}
              inputProps={{ readOnly: true }}
              format="money"
              fullWidth
              required
            />
          </Grid>
        </Grid>
        <div className="invoice-breakdown">
          <DisplayTotal money={form?.moneda?.value} total={monto.toDecimal()} />
        </div>
      </div>
      <Divider className="separator" />
      <div className="actions">
        <Button
          id="addPayComplementBtn"
          variant="contained"
          type="submit"
          color="secondary"
          activity={addMutation.isLoading}
          disabled={!valid || isInvoiceFullyPaid || isAmountExceedingBalance}>
          Agregar
        </Button>
      </div>
    </IOCustomForm>
  )
}

export default PaymentComplementForm
